<script setup lang="ts">
import type {
  FaceDemographicField,
  FaceSelectionFace,
} from './faceSelectionTypes';

interface Props {
  face: FaceSelectionFace;
  getDropdownLabel: (
    face: FaceSelectionFace,
    field: FaceDemographicField
  ) => string;
  selected: boolean;
  // The selection is full and this face is not in it: a tap does nothing.
  unavailable?: boolean;
  showAgeSelector: boolean;
  showGenderSelector: boolean;
  showRaceSelector: boolean;
}

interface Emits {
  dropdownOpen: [
    face: FaceSelectionFace,
    field: FaceDemographicField,
    event: MouseEvent,
  ];
  faceClick: [face: FaceSelectionFace];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onDropdownClick(field: FaceDemographicField, event: MouseEvent): void {
  emit('dropdownOpen', props.face, field, event);
}
</script>

<template>
  <li
    class="FacesIndividual__select-item"
    data-testid="faces-select-item"
    :data-face-uuid="face.uuid"
    :data-selected="selected ? 'true' : undefined"
    :data-unavailable="unavailable ? 'true' : undefined"
    :class="{
      'FacesIndividual__select-item--selected': selected,
      'FacesIndividual__select-item--unavailable': unavailable,
    }"
  >
    <div
      class="FacesIndividual__select-clickable"
      data-testid="faces-select-clickable"
      @click="emit('faceClick', face)"
    >
      <i class="FacesIndividual__select-icon kiosk-success-icon" />
      <div class="FacesIndividual__select-inner sentry-block">
        <img
          class="FacesIndividual__select-img"
          data-testid="faces-image"
          :data-face-uuid="face.uuid"
          :src="face.url ?? undefined"
        />
      </div>
    </div>
    <div
      v-if="showGenderSelector"
      class="kiosk-select FacesIndividual__gender-select FacesIndividual__dropdown-trigger sentry-block"
      data-testid="faces-gender-select"
      @click.stop="onDropdownClick('gender', $event)"
    >
      {{ getDropdownLabel(face, 'gender') }}
    </div>
    <div
      v-if="showAgeSelector"
      class="kiosk-select FacesIndividual__age-select FacesIndividual__dropdown-trigger sentry-block"
      data-testid="faces-age-select"
      @click.stop="onDropdownClick('age_group', $event)"
    >
      {{ getDropdownLabel(face, 'age_group') }}
    </div>
    <div
      v-if="showRaceSelector"
      class="kiosk-select FacesIndividual__race-select FacesIndividual__dropdown-trigger sentry-block"
      data-testid="faces-race-select"
      @click.stop="onDropdownClick('race', $event)"
    >
      {{ getDropdownLabel(face, 'race') }}
    </div>
  </li>
</template>

<style lang="scss" scoped>
.FacesIndividual__select-item {
  width: var(--FacesIndividual__select-item-width);
  padding: var(--FacesIndividual__select-item-padding);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  &--selected {
    .FacesIndividual__select-inner {
      box-shadow:
        0 0 0 0.2rem var(--Global--success-color),
        0 0.3rem 0.6rem rgba(var(--Kiosk--tint-color-rgb), 0.3);
    }

    .FacesIndividual__select-icon {
      opacity: 1;
    }
  }

  &--unavailable .FacesIndividual__select-inner {
    opacity: 0.45;
    transition: opacity 200ms;
  }
}

.FacesIndividual__select-clickable {
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
}

.FacesIndividual__dropdown-trigger {
  overflow: hidden;
  text-overflow: ellipsis;
}

.FacesIndividual__gender-select {
  width: 100%;
  height: var(--FacesIndividual__dropdown-trigger-height);
  font-size: var(--FacesIndividual__dropdown-trigger-font-size);
}

.FacesIndividual__age-select,
.FacesIndividual__race-select {
  width: 100%;
  height: var(--FacesIndividual__dropdown-trigger-height);
  font-size: var(--FacesIndividual__dropdown-trigger-font-size);
  margin-top: 0.6rem;
}

.FacesIndividual__select-icon {
  --kiosk-success-icon-size: calc(var(--FacesIndividual__select-item-width) * 0.2);

  position: absolute;
  top: calc(var(--FacesIndividual__select-item-gap) * 0.2);
  right: calc(var(--FacesIndividual__select-item-gap) * 0.2);
  cursor: pointer;
  opacity: 0;
  transition: opacity 200ms;
}

.FacesIndividual__select-inner {
  border-radius: 0.5rem;
  overflow: hidden;
  width: 100%;
  aspect-ratio: var(--FacesIndividual__select-item-ratio);
  cursor: pointer;
  box-shadow: 0 0.1rem 0.6rem rgba(var(--Kiosk--tint-color-rgb), 0.2);
}

.FacesIndividual__select-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

</style>
